import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { customersApi } from '../../lib/api/customers';
import { billingApi, BillingOrder } from '../../lib/api/billing';
import { Customer } from './types';
import { ArrowLeft, FileText, Calendar, ExternalLink, Clock } from 'lucide-react';
import { addMonths, addYears, format } from 'date-fns';

const formatCurrency = (amount: number, currency: string) => {
    // Amount is stored in Cents (e.g. 2200), so divide by 100
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount / 100);
};

export const CustomerInvoicesPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [customer, setCustomer] = useState<Customer | null>(null);
    const [orders, setOrders] = useState<BillingOrder[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (id) {
            loadData(id);
        }
    }, [id]);

    const loadData = async (customerId: string) => {
        try {
            setLoading(true);
            const [customerData, ordersData] = await Promise.all([
                customersApi.getById(customerId),
                billingApi.getCustomerOrders(customerId)
            ]);
            setCustomer(customerData);
            setOrders(ordersData);
        } catch (error) {
            console.error('Failed to load data', error);
        } finally {
            setLoading(false);
        }
    };

    const getFutureInvoices = () => {
        if (!customer?.subscription || !customer.subscription.plan?.prices?.[0]) return [];

        const price = customer.subscription.plan.prices[0];
        const interval = price.interval; // 'month' or 'year'
        const currentPeriodEnd = new Date(customer.subscription.currentPeriodEnd || new Date());
        const amount = price.amount;
        const currency = price.currency;

        const future = [];
        let nextDate = currentPeriodEnd;

        // Generate next 3 invoices
        for (let i = 0; i < 3; i++) {
            future.push({
                date: nextDate,
                amount,
                currency,
                description: `Renewal for ${customer.subscription.plan.name}`
            });

            if (interval === 'year') {
                nextDate = addYears(nextDate, 1);
            } else {
                nextDate = addMonths(nextDate, 1);
            }
        }
        return future;
    };

    if (loading) return <div className="p-8 text-center text-neutral-500">Loading...</div>;
    if (!customer) return <div className="p-8 text-center text-red-500">Customer not found</div>;

    const futureInvoices = getFutureInvoices();

    return (
        <div className="h-full flex flex-col p-1 animate-fade-in max-w-6xl mx-auto w-full">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate(`/customers/${id}`)}
                        className="btn-ghost !p-1.5"
                    >
                        <ArrowLeft size={16} />
                    </button>
                    <div>
                        <h1 className="text-xl font-bold text-neutral-900">Invoices for {customer.name || customer.email}</h1>
                        <p className="text-xs text-neutral-500">View past and upcoming invoices</p>
                    </div>
                </div>
            </div>

            <div className="space-y-8">
                {/* Past Invoices */}
                <div className="bg-white rounded-lg border border-neutral-200 shadow-sm overflow-hidden">
                    <div className="px-4 py-3 border-b border-neutral-100 bg-neutral-50/50">
                        <h2 className="text-sm font-semibold text-neutral-900 flex items-center gap-2">
                            <FileText size={14} className="text-neutral-500" />
                            Invoice History
                        </h2>
                    </div>
                    <div>
                        {orders.length === 0 ? (
                            <div className="p-8 text-center text-neutral-500 text-sm">No invoices found.</div>
                        ) : (
                            <div className="divide-y divide-neutral-100">
                                {orders.map(order => (
                                    <div key={order.id} className="p-4 flex items-center justify-between hover:bg-neutral-50/50 transition-colors">
                                        <div className="flex items-center gap-4">
                                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${order.status === 'PAID' ? 'bg-green-100 text-green-600' :
                                                order.status === 'FAILED' ? 'bg-red-100 text-red-600' : 'bg-neutral-100 text-neutral-400'
                                                }`}>
                                                <FileText size={14} />
                                            </div>
                                            <div>
                                                <div className="text-sm font-medium text-neutral-900">
                                                    {formatCurrency(order.amount, order.currency)}
                                                    <span className={`ml-2 text-xs px-1.5 py-0.5 rounded border ${order.status === 'PAID' ? 'bg-green-50 text-green-700 border-green-100' :
                                                        order.status === 'FAILED' ? 'bg-red-50 text-red-700 border-red-100' :
                                                            'bg-neutral-50 text-neutral-600 border-neutral-100'
                                                        }`}>{order.status}</span>
                                                </div>
                                                <div className="text-xs text-neutral-500 flex items-center gap-2 mt-0.5">
                                                    <span>{format(new Date(order.date), 'MMMM d, yyyy')}</span>
                                                    <span>•</span>
                                                    <span>Order #{order.orderNumber}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            {order.hostedInvoiceUrl && (
                                                <a
                                                    href={order.hostedInvoiceUrl}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 hover:underline"
                                                >
                                                    View <ExternalLink size={10} />
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Future Invoices */}
                <div className="bg-white rounded-lg border border-neutral-200 shadow-sm overflow-hidden bg-gradient-to-br from-white to-neutral-50/50">
                    <div className="px-4 py-3 border-b border-neutral-100 bg-neutral-50/50">
                        <h2 className="text-sm font-semibold text-neutral-900 flex items-center gap-2">
                            <Clock size={14} className="text-neutral-500" />
                            Upcoming Invoices (Estimated)
                        </h2>
                    </div>
                    {futureInvoices.length === 0 ? (
                        <div className="p-8 text-center text-neutral-500 text-sm">No scheduled invoices found (active subscription required).</div>
                    ) : (
                        <div className="divide-y divide-neutral-100">
                            {futureInvoices.map((inv, i) => (
                                <div key={i} className="p-4 flex items-center justify-between opacity-75 hover:opacity-100 transition-opacity">
                                    <div className="flex items-center gap-4">
                                        <div className="w-8 h-8 rounded-full bg-neutral-100 flex items-center justify-center text-neutral-400 border border-neutral-200 border-dashed">
                                            <Calendar size={14} />
                                        </div>
                                        <div>
                                            <div className="text-sm font-medium text-neutral-700">
                                                {formatCurrency(inv.amount, inv.currency)}
                                                <span className="ml-2 text-xs px-1.5 py-0.5 rounded border bg-neutral-100 text-neutral-500 border-neutral-200">
                                                    Scheduled
                                                </span>
                                            </div>
                                            <div className="text-xs text-neutral-500 mt-0.5">
                                                {format(inv.date, 'MMMM d, yyyy')} • {inv.description}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    <div className="px-4 py-3 bg-neutral-50/30 text-xs text-neutral-400 border-t border-neutral-100 italic">
                        * Future invoice dates and amounts are estimates based on the current subscription plan.
                    </div>
                </div>
            </div>
        </div>
    );
};
